import { translationKey } from '../../../i18n';
import { useCallback, useEffect, useState, useRef, useId } from 'react';
import { checkpointPermissionDraft } from '../../../utils/permissionDraft';

const PERMISSION_HELP_KEY = 'pill_reminder_ios_alarm_permission_help';
const STATUS_ERROR = translationKey('알림 설정을 확인하지 못했어요. 다시 확인해주세요.');

function readPermissionHelp() {
    try { return localStorage.getItem(PERMISSION_HELP_KEY) === 'true'; }
    catch (_) { return false; }
}

export default function useIosRingtoneMode(platform, supported) {
    const changing = useRef(false);
    const requested = useRef(null);
    const queryTimer = useRef(null);
    const changeTimer = useRef(null);
    const requestSequence = useRef(0);
    const requestScope = useId().replace(/:/g, '_');
    const activeRequest = useRef(null);
    const lastStatusRevision = useRef(null);
    const nextRequestId = useCallback(() => {
        activeRequest.current = `${requestScope}-${++requestSequence.current}`;
        return activeRequest.current;
    }, [requestScope]);
    const permissionHelpRef = useRef(readPermissionHelp());
    const [permissionHelp, setPermissionHelp] = useState(permissionHelpRef.current);
    const [requestedMode, setRequestedMode] = useState(null);
    const [mode, setMode] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const rememberPermissionHelp = useCallback((needed) => {
        permissionHelpRef.current = needed;
        setPermissionHelp(needed);
        // This records that help was needed, never the current OS permission.
        // A fresh native status is still required before showing the help.
        try {
            if (needed) localStorage.setItem(PERMISSION_HELP_KEY, 'true');
            else localStorage.removeItem(PERMISSION_HELP_KEY);
        } catch (_) {}
    }, []);
    const clearQueryTimer = useCallback(() => {
        if (queryTimer.current !== null) window.clearTimeout(queryTimer.current);
        queryTimer.current = null;
    }, []);
    const clearChangeTimer = useCallback(() => {
        if (changeTimer.current !== null) window.clearTimeout(changeTimer.current);
        changeTimer.current = null;
    }, []);
    const query = useCallback(() => {
        if (platform !== 'ios' || !supported || changing.current || queryTimer.current !== null) return;
        // Another mounted editor can disable the mode and clear its recovery hint.
        // Reconcile that hint before interpreting the fresh native mode response.
        const currentHelp = readPermissionHelp();
        permissionHelpRef.current = currentHelp;
        setPermissionHelp(currentHelp);
        try {
            if (!window.ReactNativeWebView?.postMessage) throw new Error('bridge unavailable');
            queryTimer.current = window.setTimeout(() => {
                queryTimer.current = null;
                setStatusError(STATUS_ERROR);
            }, 10000);
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'GET_IOS_RINGTONE_MODE', requestId: nextRequestId() }));
        } catch (_) {
            clearQueryTimer();
            setStatusError(STATUS_ERROR);
        }
    }, [platform, supported, clearQueryTimer, nextRequestId]);
    useEffect(() => {
        if (platform !== 'ios' || !supported) return;
        const handler = (event) => {
            let msg;
            try { msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; }
            catch (_) { return; }
            if (msg?.type !== 'IOS_RINGTONE_MODE_STATUS') return;
            if (Number.isInteger(msg.webviewSession) && Number.isInteger(window.__NATIVE_WEBVIEW_SESSION)
                && msg.webviewSession !== window.__NATIVE_WEBVIEW_SESSION) return;
            // Updated app JS echoes request IDs. Older installed bundles remain compatible.
            if (msg.requestId && msg.requestId !== activeRequest.current) return;
            if (changing.current && !msg.changeResult) return;
            if (Number.isInteger(msg.statusRevision)) {
                if (lastStatusRevision.current !== null && msg.statusRevision < lastStatusRevision.current) return;
                lastStatusRevision.current = msg.statusRevision;
            }
            clearQueryTimer();
            if (msg.requestId) activeRequest.current = null;
            if (msg.changeResult) {
                clearChangeTimer();
                changing.current = false;
                setBusy(false);
                setError(msg.error || null);
            }
            const next = msg.data;
            if (!next || typeof next.selectable !== 'boolean' || typeof next.useAlarmKit !== 'boolean') {
                // Never replace a known mode with the native status-read failure payload.
                setStatusError(STATUS_ERROR);
                if (msg.changeResult) setError(msg.error || STATUS_ERROR);
                return;
            }
            setMode(next);
            setStatusError(null);
            const permissionRecovered = permissionHelpRef.current && next.authorized === true;
            if (next.authorized === true) rememberPermissionHelp(false);
            else if (next.selectable && next.authorized === false
                && (next.useAlarmKit || requested.current === true)) {
                rememberPermissionHelp(true);
            }
            if (msg.changeResult) setError(msg.error || null);
            else if (permissionRecovered || (next.useAlarmKit === requested.current && !next.pending)) setError(null);
            // A routine focus/poll response must not erase a failed change or permission help.
        };
        window.addEventListener('message', handler);
        document.addEventListener('message', handler);
        window.addEventListener('focus', query);
        window.addEventListener('app-runtime-info-updated', query);
        query();
        return () => {
            clearQueryTimer();
            clearChangeTimer();
            window.removeEventListener('message', handler);
            document.removeEventListener('message', handler);
            window.removeEventListener('focus', query);
            window.removeEventListener('app-runtime-info-updated', query);
        };
    }, [platform, query, supported, clearQueryTimer, clearChangeTimer, rememberPermissionHelp]);
    useEffect(() => {
        if (platform !== 'ios' || !supported) return undefined;
        const recheck = () => { if (!document.hidden) query(); };
        document.addEventListener('visibilitychange', recheck);
        const permissionMissing = (mode?.authorized === false && (mode?.useAlarmKit || permissionHelp))
            || (mode?.useAlarmKit === false && mode?.criticalGranted === false);
        const timer = permissionMissing ? window.setInterval(recheck, 2000) : null;
        return () => {
            document.removeEventListener('visibilitychange', recheck);
            if (timer !== null) window.clearInterval(timer);
        };
    }, [platform, supported, mode?.useAlarmKit, mode?.authorized, mode?.criticalGranted, permissionHelp, query]);
    const change = useCallback((useAlarmKit) => {
        if (platform !== 'ios' || !supported || changing.current || !mode?.selectable || statusError) return;
        clearQueryTimer();
        requested.current = useAlarmKit;
        setRequestedMode(useAlarmKit);
        changing.current = true;
        setBusy(true);
        setError(null);
        if (!useAlarmKit) rememberPermissionHelp(false);
        try {
            if (!window.ReactNativeWebView?.postMessage) throw new Error('bridge unavailable');
            // A native change may complete while its reply is lost. Keep the last known
            // mode and require a fresh status rather than leaving the switch locked.
            changeTimer.current = window.setTimeout(() => {
                changeTimer.current = null;
                changing.current = false;
                activeRequest.current = null;
                setBusy(false);
                setStatusError(STATUS_ERROR);
            }, 20000);
            checkpointPermissionDraft();
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SET_IOS_RINGTONE_MODE', requestId: nextRequestId(), data: { useAlarmKit } }));
        } catch (_) {
            clearChangeTimer();
            changing.current = false;
            setBusy(false);
            setStatusError(STATUS_ERROR);
        }
    }, [platform, supported, mode?.selectable, statusError, clearQueryTimer, clearChangeTimer, rememberPermissionHelp, nextRequestId]);
    return { mode, busy, error, statusError, permissionHelp, requestedMode, change, query };
}
